/**
 * User role setup service, backed by Firestore.
 *
 * Data layout:
 *   users/{uid}                              jobRole, isManagement, roleCompleted
 *   users/{uid}/responsibilities/{respId}    { responsibilityId, displayOrder }
 *   users/{uid}/frameworks/{respId}          { frameworks: [{promptId, isGlobalPrompt}] }
 *                                            doc presence = the user has saved an
 *                                            explicit choice (possibly empty)
 *   responsibilities/{respId}.frameworks     role-default frameworks (admin-managed)
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { auth, db, orgIdFromEmail } from '../../../../shared/firebase';
import { fetchPromptById } from '../../../../shared/data/promptsRepo';

const now = () => new Date().toISOString();

function requireUser() {
  const fbUser = auth.currentUser;
  if (!fbUser) throw new Error('Not signed in');
  return { uid: fbUser.uid, orgId: orgIdFromEmail(fbUser.email) };
}

/**
 * Set user's role and management status, and assign the role's default
 * responsibilities (mirrors the old server-side assignDefaultSetup).
 * @returns {Promise<Object>} Response with success status and user data
 */
export const setUserRole = async (jobRole, isManagement) => {
  try {
    const { uid } = requireUser();
    const isManagementBool = isManagement === true || isManagement === 'true' || isManagement === 1;

    await updateDoc(doc(db, 'users', uid), {
      jobRole,
      isManagement: isManagementBool,
      roleCompleted: true,
    });

    // Assign the role's responsibilities as defaults (only when the user has
    // none yet, so re-running role setup doesn't wipe their customisation).
    const existing = await getDocs(collection(db, 'users', uid, 'responsibilities'));
    if (existing.empty) {
      const rolesSnap = await getDocs(collection(db, 'roles'));
      let roleId = null;
      rolesSnap.forEach((d) => {
        if (d.data().roleName === jobRole) roleId = d.data().roleId;
      });
      if (roleId) {
        const respSnap = await getDocs(
          query(collection(db, 'responsibilities'), where('roleId', '==', roleId))
        );
        const batch = writeBatch(db);
        let order = 0;
        respSnap.forEach((d) => {
          batch.set(doc(db, 'users', uid, 'responsibilities', d.id), {
            responsibilityId: d.id,
            displayOrder: order,
            createdAt: now(),
          });
          order += 1;
        });
        await batch.commit();
      }
    }

    const userSnap = await getDoc(doc(db, 'users', uid));
    return { success: true, user: userSnap.data() };
  } catch (error) {
    console.error('Error setting user role:', error);
    throw error;
  }
};

/**
 * Get user's role setup (responsibilities and frameworks with prompt data).
 * @returns {Promise<Object>} { user, responsibilities, frameworks }
 */
export const getUserRoleSetup = async () => {
  try {
    const { uid } = requireUser();

    const [userSnap, userRespSnap, userFwSnap] = await Promise.all([
      getDoc(doc(db, 'users', uid)),
      getDocs(collection(db, 'users', uid, 'responsibilities')),
      getDocs(collection(db, 'users', uid, 'frameworks')),
    ]);
    if (!userSnap.exists()) {
      throw new Error('User not found');
    }
    const user = userSnap.data();

    // Responsibility selections + display order
    const selections = [];
    userRespSnap.forEach((d) => selections.push(d.data()));
    selections.sort((a, b) => {
      const ao = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const bo = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
      return ao - bo || (a.createdAt || '').localeCompare(b.createdAt || '');
    });

    // Resolve responsibility details (which also carry role-default frameworks)
    const respDocs = await Promise.all(
      selections.map((s) => getDoc(doc(db, 'responsibilities', s.responsibilityId)))
    );
    const responsibilities = [];
    const defaultFrameworks = {};
    respDocs.forEach((snap, i) => {
      if (!snap.exists()) return;
      const data = snap.data();
      responsibilities.push({ ...data, displayOrder: selections[i].displayOrder ?? null });
      defaultFrameworks[data.responsibilityId] = Array.isArray(data.frameworks)
        ? data.frameworks.map((f) => ({
            promptId: f.promptId,
            isGlobalPrompt: f.isGlobalPrompt === true,
          }))
        : [];
    });

    // User-saved frameworks override role defaults (including explicit empty)
    const savedFrameworks = {};
    userFwSnap.forEach((d) => {
      const data = d.data();
      savedFrameworks[d.id] = Array.isArray(data.frameworks) ? data.frameworks : [];
    });

    const frameworksByResponsibility = {};
    responsibilities.forEach((resp) => {
      const respId = resp.responsibilityId;
      frameworksByResponsibility[respId] =
        savedFrameworks[respId] !== undefined
          ? savedFrameworks[respId]
          : defaultFrameworks[respId] || [];
    });

    // Resolve prompt data for every framework entry
    const frameworks = {};
    await Promise.all(
      Object.entries(frameworksByResponsibility).map(async ([respId, entries]) => {
        frameworks[respId] = [];
        const prompts = await Promise.all(
          entries.map((entry) => fetchPromptById(entry.promptId).catch(() => null))
        );
        prompts.forEach((prompt, i) => {
          if (!prompt) return;
          frameworks[respId].push({
            promptId: entries[i].promptId,
            isGlobalPrompt: entries[i].isGlobalPrompt === true,
            prompt,
          });
        });
      })
    );

    return {
      user: {
        userId: uid,
        jobRole: user.jobRole,
        isManagement: user.isManagement === true,
        roleCompleted: user.roleCompleted === true,
      },
      responsibilities,
      frameworks,
    };
  } catch (error) {
    console.error('Error fetching user role setup:', error);
    throw error;
  }
};

/**
 * Update user's responsibilities (replaces the selection).
 * Accepts ['id1', ...] or [{ responsibilityId, displayOrder }, ...].
 * @returns {Promise<Object>} Response with success status
 */
export const updateResponsibilities = async (responsibilityIds) => {
  try {
    if (!Array.isArray(responsibilityIds)) {
      throw new Error('responsibilityIds must be an array');
    }
    const { uid } = requireUser();

    const normalized = responsibilityIds.map((entry, index) =>
      typeof entry === 'string'
        ? { responsibilityId: entry, displayOrder: index }
        : { responsibilityId: entry.responsibilityId, displayOrder: entry.displayOrder ?? index }
    );

    const existing = await getDocs(collection(db, 'users', uid, 'responsibilities'));
    const batch = writeBatch(db);
    const keep = new Set(normalized.map((e) => e.responsibilityId));
    existing.forEach((d) => {
      if (!keep.has(d.id)) batch.delete(d.ref);
    });
    normalized.forEach((entry) => {
      batch.set(doc(db, 'users', uid, 'responsibilities', entry.responsibilityId), {
        responsibilityId: entry.responsibilityId,
        displayOrder: entry.displayOrder,
        createdAt: now(),
      });
    });
    await batch.commit();

    return { success: true };
  } catch (error) {
    console.error('Error updating responsibilities:', error);
    throw error;
  }
};

/**
 * Update frameworks for a responsibility (an empty list is a valid explicit choice).
 * @returns {Promise<Object>} Response with success status
 */
export const updateFrameworks = async (responsibilityId, promptIds, isGlobalPrompts) => {
  try {
    if (!Array.isArray(promptIds)) {
      throw new Error('promptIds must be an array');
    }
    if (!Array.isArray(isGlobalPrompts) || promptIds.length !== isGlobalPrompts.length) {
      throw new Error('isGlobalPrompts must be an array with same length as promptIds');
    }
    const { uid } = requireUser();
    await setDoc(doc(db, 'users', uid, 'frameworks', responsibilityId), {
      responsibilityId,
      frameworks: promptIds.map((promptId, i) => ({
        promptId,
        isGlobalPrompt: isGlobalPrompts[i] === true,
      })),
      updatedAt: now(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating frameworks:', error);
    throw error;
  }
};

/** Remove a responsibility's saved frameworks (reverts to role defaults). */
export const clearFrameworks = async (responsibilityId) => {
  const { uid } = requireUser();
  await deleteDoc(doc(db, 'users', uid, 'frameworks', responsibilityId));
  return { success: true };
};
